<script lang="ts">
  // The ride's terrain, as the race card shows it: the altitude profile with the three numbers that
  // describe it -- how far, how much climbing, and how much that climbing mattered (KGE) -- read
  // straight off the graphic. Short enough to sit in the top half of the card next to the title.
  import { cn } from '@/lib/utils';
  import { kmToMile, meterToFoot, useImperialUnits, } from '@/Util/utils';
  import { altitudeProfile, type DashboardActivity } from './activityCardUtils';

  export let exercise: DashboardActivity;
  /**
   * Roughly one gauge column tall, so a row of these lines up with the strip below it. Beside the
   * title it gets the height of that whole half instead.
   */
  export let height: number = 52;

  const imperial = useImperialUnits();

  // The polyline is drawn in its own coordinate space and stretched to whatever width the card
  // gives it (preserveAspectRatio="none"). An elevation profile has no true aspect ratio -- the
  // race card picks a different one at every size -- so nothing is lost by letting it fill.
  const VIEW_W = 100;
  const VIEW_H = 30;
  /** Leaves the peak clear of the top edge and keeps a sliver of ground under a flat ride. */
  const TOP_PAD = 3;
  const FLOOR_PAD = 2;

  const round = (v: number, dp = 0) => {
    const f = Math.pow(10, dp);
    return Math.round(v * f) / f;
  };

  $: points = altitudeProfile(exercise);
  $: area = buildArea(points);

  /** The profile as a closed polygon: the trace, then back along the baseline to fill under it. */
  function buildArea(values: number[]): string | null {
    if (values.length < 2) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const usable = VIEW_H - TOP_PAD - FLOOR_PAD;
    const trace = values.map((value, index) => {
      const x = round((index / (values.length - 1)) * VIEW_W, 2);
      const y = round(VIEW_H - FLOOR_PAD - ((value - min) / span) * usable, 2);
      return `${x},${y}`;
    });
    return `0,${VIEW_H} ${trace.join(' ')} ${VIEW_W},${VIEW_H}`;
  }

  type Stat = { label: string; value: string; unit?: string; title?: string };
  let stats: Stat[] = [];
  $: stats = ([
    exercise?.distance
      ? {
          label: 'Distance',
          value: String(round(imperial ? kmToMile(exercise.distance) : exercise.distance, 1)),
          unit: imperial ? 'mi' : 'km',
        }
      : null,
    exercise?.elevation
      ? {
          label: 'Elevation',
          value: round(imperial ? meterToFoot(exercise.elevation) : exercise.elevation).toLocaleString(),
          unit: imperial ? 'ft' : 'm',
        }
      : null,
    exercise?.kge != null && isFinite(exercise.kge)
      ? {
          label: 'KGE',
          value: exercise.kge.toFixed(2),
          title: "Grade-dependent power scaling exponent for this ride's terrain",
        }
      : null,
  ] as (Stat | null)[]).filter((stat): stat is Stat => stat !== null);
</script>

<!-- No profile means no terrain: the numbers read off it would be about a ride that never went
     anywhere, so the whole block stands down rather than showing them over an empty box. -->
{#if area && stats.length}
  <div class="relative w-full min-w-0" style="height: {height}px">
    <svg
      class="absolute inset-0 h-full w-full rounded"
      viewBox="0 0 {VIEW_W} {VIEW_H}"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polyline points={area} class="fill-xerteal-600/20" />
    </svg>
    <!-- The numbers sit over the profile rather than beside it, so the whole block stays as short
         as a gauge column. -->
    <div class={cn('absolute inset-0 grid items-center', stats.length === 3 ? 'grid-cols-3' : stats.length === 2 ? 'grid-cols-2' : 'grid-cols-1')}>
      {#each stats as stat}
        <div class="min-w-0 text-center" title={stat.title ?? stat.label}>
          <div class="text-base font-semibold leading-tight whitespace-nowrap">
            {stat.value}{#if stat.unit}<span class="ml-0.5 text-xs font-normal">{stat.unit}</span>{/if}
          </div>
          <div class="text-[10px] uppercase tracking-wider text-muted-foreground leading-none truncate">{stat.label}</div>
        </div>
      {/each}
    </div>
  </div>
{/if}
